import { useEffect, useState } from "react";

// Global online/offline status indicator. Pins to the top of the
// viewport when navigator.onLine flips false; auto-hides after a
// brief "back online" pulse when connectivity returns.
//
// Three states:
//   - online (steady)         → render nothing
//   - offline                 → fixed red banner with "You're offline"
//   - just-came-back-online   → brief 2-second green "Back online" pulse
//
// Lives in App.tsx outside Suspense so it survives lazy-route
// transitions and is always visible (z-50, above the paywall modal's
// own z-50 — wait, paywall is z-50 too; use z-[60] to win).

export default function OfflineBanner() {
  const [state, setState] = useState<"online" | "offline" | "back-online">(
    typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "online",
  );

  useEffect(() => {
    const goOffline = () => setState("offline");
    const goOnline = () => {
      setState("back-online");
      // Auto-dismiss the "back online" pulse after 2 seconds.
      window.setTimeout(() => setState("online"), 2000);
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  if (state === "online") return null;

  const isOffline = state === "offline";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-0 inset-x-0 z-[60] py-2 px-4 text-center text-sm font-medium shadow-lg ${
        isOffline
          ? "bg-red-600 text-white"
          : "bg-emerald-600 text-white"
      }`}
      style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
    >
      {isOffline ? (
        <>
          <span className="mr-2">⚠</span>
          You're offline — some features will resume when you reconnect.
        </>
      ) : (
        <>
          <span className="mr-2">✓</span>
          Back online.
        </>
      )}
    </div>
  );
}
